import { useState, type FormEvent } from 'react';
import { parseRubToKopecks } from '@shared/types';
import type { CardLimits } from '@shared/ledger';

import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/Field';
import { InlineError } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/Toast';
import { useUpdateCardLimits } from '@/lib/mutations';
import { toMessage } from '@/lib/api';

/**
 * Editing one card's three ceilings.
 *
 * Amounts are typed in roubles and parsed with the shared `parseRubToKopecks`,
 * which accepts a comma or a period and the spaces the interface itself prints
 * — a trader typing an amount back the way they just read it must not get NaN.
 * Everything leaves here as integer kopecks.
 *
 * Zero means unlimited for the two money ceilings, and the fields say so.
 * It does not mean unlimited for the concurrent count — a card that may hold
 * zero payments at once holds none at all — so that field has its own floor of
 * one rather than a shared rule bent to fit.
 */

interface Errors {
  daily?: string;
  perPayment?: string;
  concurrent?: string;
}

/** Whole roubles stay whole: «100000» is easier to edit than «100 000,00». */
function toEditable(kopecks: number): string {
  return kopecks % 100 === 0 ? String(kopecks / 100) : (kopecks / 100).toFixed(2).replace('.', ',');
}

export function CardLimitsForm({
  cardId,
  limits,
  onDone,
}: {
  cardId: string;
  limits: CardLimits;
  onDone: () => void;
}) {
  const toast = useToast();
  const updateLimits = useUpdateCardLimits();

  const [daily, setDaily] = useState(() => toEditable(limits.dailyLimitRub));
  const [perPayment, setPerPayment] = useState(() => toEditable(limits.perPaymentLimitRub));
  const [concurrent, setConcurrent] = useState(() => String(limits.concurrentLimit));
  const [errors, setErrors] = useState<Errors>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setServerError(null);

    const dailyKopecks = parseRubToKopecks(daily);
    const perPaymentKopecks = parseRubToKopecks(perPayment);
    const concurrentCount = Number(concurrent.trim());

    const found: Errors = {};

    if (Number.isNaN(dailyKopecks)) {
      found.daily = 'Введите сумму в рублях или 0 — без ограничения.';
    } else if (dailyKopecks < 0) {
      found.daily = 'Лимит не может быть отрицательным.';
    }

    if (Number.isNaN(perPaymentKopecks)) {
      found.perPayment = 'Введите сумму в рублях или 0 — без ограничения.';
    } else if (perPaymentKopecks < 0) {
      found.perPayment = 'Лимит не может быть отрицательным.';
    }

    if (!Number.isInteger(concurrentCount) || concurrentCount < 1) {
      found.concurrent = 'Целое число, не меньше 1. Карта без одновременных платежей не примет ни одного.';
    }

    setErrors(found);
    if (Object.keys(found).length > 0) return;

    try {
      await updateLimits.mutateAsync({
        cardId,
        limits: {
          dailyLimitRub: dailyKopecks,
          perPaymentLimitRub: perPaymentKopecks,
          concurrentLimit: concurrentCount,
        },
      });
      toast.success('Лимиты сохранены');
      onDone();
    } catch (error) {
      setServerError(toMessage(error, 'Не удалось сохранить лимиты.'));
    }
  };

  return (
    <form onSubmit={(event) => void submit(event)} noValidate className="mt-3">
      {serverError ? <InlineError className="mb-3" message={serverError} /> : null}

      <TextField
        label="Лимит за сутки, ₽"
        value={daily}
        onChange={(event) => setDaily(event.target.value)}
        error={errors.daily}
        hint="Скользящие 24 часа. 0 — без ограничения."
        inputMode="decimal"
        autoComplete="off"
        monospace
      />

      <TextField
        containerClassName="mt-4"
        label="Лимит на один платёж, ₽"
        value={perPayment}
        onChange={(event) => setPerPayment(event.target.value)}
        error={errors.perPayment}
        hint="Платежи крупнее уйдут на другую карту. 0 — без ограничения."
        inputMode="decimal"
        autoComplete="off"
        monospace
      />

      <TextField
        containerClassName="mt-4"
        label="Одновременных платежей"
        value={concurrent}
        onChange={(event) => setConcurrent(event.target.value)}
        error={errors.concurrent}
        hint="Сколько незакрытых платежей может идти на эту карту сразу. Не меньше 1."
        inputMode="numeric"
        autoComplete="off"
        monospace
      />

      <div className="mt-5 flex flex-col gap-2 sm:flex-row">
        <Button type="submit" variant="primary" isLoading={updateLimits.isPending}>
          Сохранить лимиты
        </Button>
        <Button type="button" onClick={onDone} disabled={updateLimits.isPending}>
          Отмена
        </Button>
      </div>
    </form>
  );
}
